import { useLogin, useLogout, useMe } from '@/api'
import type { User } from '@/api'
import { type ReactNode, createContext, useCallback, useContext, useState } from 'react'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingOut: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isLoggingOut, setIsLoggingOut] = useState(false)

  const { data: user, isLoading: isLoadingUser } = useMe()
  const loginMutation = useLogin()
  const logoutMutation = useLogout()

  const login = useCallback(
    async (email: string, password: string) => {
      setIsLoggingOut(false)
      await loginMutation.mutateAsync({ email, password })
    },
    [loginMutation]
  )

  const logout = useCallback(async () => {
    setIsLoggingOut(true)
    await logoutMutation.mutateAsync()
  }, [logoutMutation])

  const isLoading = isLoadingUser || loginMutation.isPending || logoutMutation.isPending

  return (
    <AuthContext.Provider
      value={{
        user: user ?? null,
        isAuthenticated: !!user,
        isLoading,
        isLoggingOut,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}
